import React, { useEffect } from 'react';
import BasePageContainer from 'src/components/CommonV2Components/BasePageContainer/BasePageContainer';

import { BackNextButtons } from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/BudgetBuilder/common';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { BEACON_CHART_SPACING_BUDGET } from 'src/components/Layout/Beacon/BeaconProLayoutConsts';
import { useAppSelector, useHistory, useSnackbar } from 'src/utils/Hooks';
import _isEmpty from 'lodash/isEmpty';
import { BEACON_SUBTABS } from 'src/components/BeaconV2Components/GenericSidebarNav/useBeaconRoutes';
import ConfirmationDetail from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/BudgetBuilder/BudgetConfirmation/ConfirmationDetails';

import axios from 'axios';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import _pickBy from 'lodash/pickBy';
import { AD_BUDGET_TYPE } from 'sl-ad-campaign-manager-data-model';
import moment from 'moment';

const getRemoteToday = (timezone: string) => {
  return moment().tz(timezone).format('YYYY-MM-DD');
};

const endOfCurrentMonth = moment().endOf('month').format('YYYY-MM-DD');
const startOfNextMonth = moment().add(1, 'M').startOf('month').format('YYYY-MM-DD');
const endOfNextMonth = moment().add(1, 'M').endOf('month').format('YYYY-MM-DD');

const getTotalEntityBudget = (portfolios?: any) => {
  return portfolios?.reduce((total: number, portfolio: any) => {
    const currentBudget =
      portfolio?.entity?.extendedAttributes?.newBudgetSetting?.amount ||
      portfolio?.entity?.extendedAttributes?.liveBudgetSetting?.amount ||
      0;
    return total + currentBudget;
  }, 0);
};

const BudgetConfirmation = () => {
  const { showSnackbar } = useSnackbar();
  const history = useHistory();
  const { queryParams } = useAppSelector((state) => state.app);
  const { searchParams } = queryParams;
  const { mainEntity } = useAppSelector((state) => state.entityService);
  const portfolios = useAppSelector((state) => state.adBudgetEditor.portfolioArray);

  const totalAmount = getTotalEntityBudget(portfolios);
  const { hashId } = mainEntity;
  const selectedEntity = useAppSelector((state) => state.adBudgetEditor.entity);
  const selectTimeline = useAppSelector((state) => state.adBudgetEditor.timeline);

  const hasEntityData = !_isEmpty(selectedEntity) && !!selectTimeline;

  const [submitting, setSubmitting] = React.useState(false);
  const adBudgetEditor = useAppSelector((state) => state.adBudgetEditor);

  const timeline = useAppSelector((state) => state.adBudgetEditor.timeline);

  const formatAndSubmitData = async () => {
    try {
      setSubmitting(true);
      const entity = adBudgetEditor.entity as any;
      const timelineName = timeline.name;
      const { timeZone } = adBudgetEditor.platform as any;

      const SUBMIT_API = '/apiAdManager/adUpdateRequests/bulkBudgetUpdate';

      const bulkBudgetUpdateRequest = {
        beaconClientId: entity?.beaconClientId,
        platformType: entity.platformType,
        updateType: 'bulkBudgetUpdate',
        extendedAttributes: {
          adEntitiesBudgetSetting: [
            {
              beaconClientLoginId: entity.extendedAttributes.beaconClientLoginId,
              entityId: entity.extendedAttributes.entityId,
              entityIdApi: entity.extendedAttributes.entityIdApi,
              entityIdUi: entity.extendedAttributes.entityIdUi,
              [`${timelineName}`]: {
                amount: totalAmount,
                budgetType: AD_BUDGET_TYPE.MONTHLY
              }
            }
          ],
          adPortfoliosBudgetSetting: [] as {
            beaconClientLoginId: number;
            entityId: string;
            entityIdApi: string;
            entityIdUi: string;
            portfolioId: string;
            portfolioIdApi: string;
            portfolioIdUi?: string;
            currentMonthBudgetSetting?: {
              amount: number;
              startDate?: Date;
              endDate?: Date;
              budgetType: AD_BUDGET_TYPE.MONTHLY;
            };
            nextMonthBudgetSetting?: {
              amount: number;
              startDate?: Date;
              endDate?: Date;
              budgetType: AD_BUDGET_TYPE.MONTHLY;
            };
          }[],
          adCampaignsBudgetSetting: []
        }
      };

      adBudgetEditor.portfolioArray.forEach(({ entity: portfolio }: { entity: any }) => {
        const dateOverwrite: any = {
          endDate: timeline.id === 0 ? endOfCurrentMonth : endOfNextMonth
        };

        if (timeline.id !== 0) {
          dateOverwrite.startDate = startOfNextMonth;
        }

        if (timeline.id === 0) {
          const oldStartDate = _get(
            portfolio,
            ['extendedAttributes', 'currentMonthBudgetSetting', 'startDate'],
            undefined
          );

          if (!oldStartDate) {
            const remoteToday = getRemoteToday(timeZone);
            dateOverwrite.startDate = remoteToday;
          } else {
            dateOverwrite.startDate = undefined;
          }
        }

        const newBudgetAmount = _get(portfolio, ['extendedAttributes', 'newBudgetSetting', 'amount'], undefined);
        const isSelected = _get(portfolio, ['extendedAttributes', 'isSelected']);
        if (!portfolio.isUnassigned && isSelected && newBudgetAmount !== undefined) {
          const {
            extendedAttributes: {
              beaconClientLoginId,
              entityId,
              entityIdApi,
              entityIdUi,
              portfolioId,
              portfolioIdApi,
              portfolioIdUi,
              currentMonthBudgetSetting
            }
          } = portfolio;

          bulkBudgetUpdateRequest.extendedAttributes.adPortfoliosBudgetSetting.push({
            beaconClientLoginId,
            entityId,
            entityIdApi,
            entityIdUi,
            portfolioId,
            portfolioIdApi,
            portfolioIdUi,
            [`${timelineName}`]: _pickBy(
              {
                ...currentMonthBudgetSetting,
                ...dateOverwrite,
                amount: newBudgetAmount,
                budgetType: AD_BUDGET_TYPE.MONTHLY_RECURRING
              },
              (item) => item !== undefined
            )
          });
        }
      });

      await axios.post(SUBMIT_API, bulkBudgetUpdateRequest);
      showSnackbar({
        message: 'Your budget was successfully updated.',
        type: 'success'
      });
    } catch (error) {
      showSnackbar({
        message: 'An error occurred. Please reach out to support@stackline.com for further guidance.',
        type: 'error'
      });
      console.error('Error submitting data', error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (!hasEntityData) {
      history.push(`/client/${hashId}${searchParams}&tab=advertising&subtab=budgetEntity`);
    }
  }, [hasEntityData]);

  const onSaveClick = async () => {
    await formatAndSubmitData();
    history.push(`/client/${hashId}${searchParams}&tab=advertising&subtab=${BEACON_SUBTABS.AD_METRICS}`);
  };

  const onBackClick = () => {
    history.push(`/client/${hashId}${searchParams}&tab=advertising&subtab=${BEACON_SUBTABS.BUDGET_ALLOCATION}`);
  };

  return (
    <BasePageContainer>
      <Flex flexDirection="column" gap={BEACON_CHART_SPACING_BUDGET}>
        <ConfirmationDetail />
        <BackNextButtons canMoveBack onSaveClick={onSaveClick} onBackClick={onBackClick} submitting={submitting} />
      </Flex>
    </BasePageContainer>
  );
};

export default BudgetConfirmation;
